import axios from 'axios';
import { useState } from 'react';
import { getApiErrorFields, getApiErrorMessage } from '@utils/api-error';
import { buildPostHeaders } from '@utils/http';
import type { DashRegistrationStep } from '@/types/dashboard';
import type { ISaveStepsPostResponse, ISaveStepsSubmitResult } from '@/types/save-steps';

const SAVE_STEPS_ENDPOINT = '/api/register/save-steps';

export interface IUseSaveStepsResult {
    submit: (newCurrentStep: DashRegistrationStep) => Promise<ISaveStepsSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

export const useSaveSteps = (registrationId: number, editKey: string): IUseSaveStepsResult => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const submit = async (newCurrentStep: DashRegistrationStep): Promise<ISaveStepsSubmitResult> => {
        if (isSubmitting) return { ok: false };

        setIsSubmitting(true);
        setSubmitError(null);

        const headers = buildPostHeaders();

        try {
            const { data } = await axios.post<ISaveStepsPostResponse>(
                SAVE_STEPS_ENDPOINT + '/' + registrationId + '/' + editKey,
                { newCurrentStep },
                { headers, withCredentials: true },
            );

            return { ok: data.success };
        } catch (err: unknown) {
            const message = getApiErrorMessage(err, 'Submission failed. Please try again.');
            const errors = getApiErrorFields(err);

            setSubmitError(message);

            return { ok: false, errors, message };
        } finally {
            setIsSubmitting(false);
        }
    };

    return { submit, isSubmitting, submitError };
};
